import css from 'styled-jsx/css';
import { display, sansSerif, serif } from 'styles/css-constants';
import { sharedPalette, warmGrays } from 'styles/colors/constants';

export const recipeStyles = css`
  .Recipe {
    border-top: 1.5rem solid ${sharedPalette.kYellow};
    padding: 1rem 0;
  }

  .Recipe :global(a) {
    color: #000;
  }

  .Recipe h3,
  .Recipe :global(.Recipe__suppliesGroupName) {
    margin-top: 0;
    font-family: ${sansSerif};
    font-size: var(--baseFontSize);
    font-weight: bold;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .Recipe :global(.Recipe__ingredient),
  .Recipe :global(.Recipe__supplyItem) {
    position: relative;
    margin-bottom: 0.75rem;
    line-height: 1.35;
  }

  /* 
* Because of a Recipe editor limitation we have to force
* text to be inline 
*/
  .Recipe :global(.Recipe__ingredient p),
  .Recipe :global(.Recipe__ingredient span),
  .Recipe :global(.Recipe__instructionsGroup p),
  .Recipe :global(.Recipe__instructionsGroup span) {
    display: inline;
  }

  .Recipe :global(.Recipe__ingredients),
  .Recipe :global(.Recipe__supplies) {
    margin: 0;
    padding-left: 0;
    list-style: none;
    font-family: ${sansSerif};
    font-size: var(--baseFontSize);
  }

  .Recipe__ingredientsSection,
  .Recipe__suppliesSection {
    border-bottom: 1px solid ${warmGrays[3]};
    background-color: #f5f3f3;
  }

  .Recipe :global(.Recipe__ingredientsGroup) {
    padding-bottom: 1rem;
  }

  .Recipe :global(.Recipe__ingredientsGroupName),
  .Recipe :global(.Recipe__instructionsGroup h4) {
    margin-top: 0rem;
    margin-bottom: 0.75rem;
    font-family: ${serif};
    font-size: var(--baseFontSize);
    font-style: italic;
    font-weight: normal;
  }

  .Recipe__ingredientsHeading,
  .Recipe__instructionsHeading,
  .Recipe :global(.Recipe__suppliesGroupName) {
    margin-bottom: 0.75rem;
  }

  .Recipe__ingredientsHeading {
    padding-top: 1.25rem;
  }

  .Recipe__ingredientsSection,
  .Recipe__suppliesSection {
    padding: 0 1rem;
  }

  .Recipe :global(.Recipe__instructions) {
    margin: 0;
    list-style: none;
    counter-reset: listCounter;
    padding: 0;
  }

  .Recipe :global(.Recipe__instructionsGroup) {
    margin-bottom: 1.25rem;
  }

  .Recipe__instructionsHeading {
    padding-top: 1.25rem;
  }

  .Recipe :global(.Recipe__instructionStep),
  .Recipe__notes {
    font-size: var(--fontScale1);
    line-height: 1.5;
  }

  .Recipe :global(.Recipe__instructionStep) {
    position: relative;
    margin-top: 0.5rem;
    border-top: 1px solid ${warmGrays[1]};
    padding-top: 0.5rem;
  }

  .Recipe :global(.Recipe__instructionStep a),
  .Recipe .Recipe__notes a {
    color: #000;
  }

  .Recipe :global(.Recipe__instructionStep p),
  .Recipe__notes :global(p) {
    margin-bottom: 1em;
  }

  .Recipe :global(.Recipe__instructionStep p) {
    margin-bottom: 0;
  }

  .Recipe :global(.Recipe__instructionStep .Figure) {
    padding-top: 1rem;
    padding-bottom: 0.5rem;
  }

  .Recipe :global(.Recipe__instructionStep:first-of-type) {
    margin-top: 0;
    border-top: none;
    padding-top: 0;
  }

  .Recipe :global(.Recipe__instructionStepContent),
  .Recipe :global(.Recipe__notes) {
    font-family: ${serif};
  }

  .Recipe :global(.Recipe__instructionStepContent) {
    margin-bottom: 0.5rem;
  }

  .Recipe :global(.Recipe__instructionStepContent::before) {
    margin-right: 0.5rem;
    counter-increment: listCounter;
    content: counter(listCounter);
    display: inline-block;
    color: ${sharedPalette.watermelon};
    font-family: ${sansSerif};
    font-weight: bold;
  }

  .Recipe__notes :global(p) {
    margin-top: 0;
  }

  .Recipe__notesHeading {
    margin-bottom: 1rem;
  }

  .Recipe__notesSection {
    border-top: 1px solid ${warmGrays[3]};
    border-bottom: 1px solid ${warmGrays[3]};
    padding-top: 1.25rem;
    padding-bottom: 0.25rem;
  }

  .Recipe__supplementalInfo {
    margin-bottom: 1.5rem;
    display: flex;
  }

  .Recipe__supplementalInfo :global(p) {
    margin-top: 0;
    margin-bottom: 0;
  }

  .Recipe :global(.Recipe__suppliesGroup) {
    padding-bottom: 0.75rem;
  }

  .Recipe__suppliesSection {
    padding-top: 1.25rem;
  }

  .Recipe :global(.Recipe__supplyItem span),
  .Recipe :global(.Recipe__supplyItem p) {
    display: inline;
  }

  .Recipe__time {
    flex: 0 0 66.66%;
    display: flex;
  }

  .Recipe :global(.Recipe__timeEntry) {
    flex: 0 0 50%;
    font-family: ${sansSerif};
    font-size: var(--fontScaleNeg2);
    line-height: 1.35;
  }

  .Recipe :global(.Recipe__timeEntry:first-child) {
    padding-right: 0.25rem;
    padding-left: 0.25rem;
  }

  .Recipe :global(.Recipe__timeEntry:nth-child(2)) {
    padding-left: 0.5rem;
  }

  .Recipe :global(.Recipe__timeLabel) {
    margin-bottom: 0.125rem;
    display: block;
    font-weight: bold;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .Recipe__title {
    margin: 0 0 1.5rem;
    font-family: ${display};
    font-size: var(--fontScale4);
    font-weight: normal;
    line-height: 1.3;
  }

  .Recipe__yield {
    flex: 0 0 33.33%;
    padding-right: 0.5rem;
    font-family: ${sansSerif};
    font-size: var(--fontScaleNeg2);
    line-height: 1.35;
  }

  .Recipe__yieldLabel {
    margin-bottom: 0.125rem;
    display: block;
    font-weight: bold;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .Recipe :global(.RecipeNutritionalGuide) {
    border-top: 1px solid ${warmGrays[3]};
    border-bottom: 1px solid ${warmGrays[3]};
    padding: 0.25rem 0;
  }

  .Recipe :global(.RecipeNutritionalGuide__nutrition) {
    padding: 1rem 1rem 0.5rem;
  }

  .Recipe :global(.RecipeNutritionalGuide__nutrition a) {
    border-bottom: none;
  }

  .Recipe :global(.RecipeNutritionalGuide header) {
    border: none;
    padding-right: 0;
    background-color: transparent;
  }

  .Recipe :global(.RecipeNutritionalGuide header button) {
    margin: 0;
    display: block;
    width: 100%;
    padding: 0.25rem;
    letter-spacing: 0.02em;
    text-align: center;
    text-transform: uppercase;
    /* override iOS defaults */
    -webkit-appearance: none;
    border: none;
    border-radius: 0;
    background-color: #fff;
  }

  .Recipe
    :global(.RecipeNutritionalGuide
      header[aria-expanded='false']
      button
      .close),
  .Recipe
    :global(.RecipeNutritionalGuide
      header[aria-expanded='true']
      button
      .expand) {
    display: none;
  }

  .Recipe :global(.RecipeNutritionalGuide header button svg) {
    margin-left: 0.25rem;
    font-size: 0.8em;
  }

  @supports (display: grid) {
    .Recipe__supplementalInfo {
      display: grid;
      grid-template-columns: calc((100% - 2rem) / 3) 1fr;
    }

    .Recipe__time {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    .Recipe :global(.Recipe__timeEntry:first-child) {
      margin-left: 0.5rem;
      border-left: 1px solid ${warmGrays[3]};
      padding-right: 0;
      padding-left: 0.5rem;
    }

    .Recipe :global(.Recipe__timeEntry:nth-child(2)) {
      margin-left: 0.5rem;
      border-left: 1px solid ${warmGrays[3]};
      padding-left: 0.5rem;
    }

    .Recipe__yield {
      padding: 0;
    }
  }
`;

export default css`
  .AmpRecipe {
    margin-bottom: 1.5rem;
  }
`;
